import { motion } from "framer-motion";
import { useAtom } from "jotai";
import React, { ReactPropTypes, useEffect, useRef } from "react";
import Icon from "../../components/Icon";
import {
  DEFAULT_CURSOR_OFFSET,
  DEFAULT_CURSOR_SIZE,
  DEFAULT_SPRING,
} from "../../consts";
import { colorValue } from "../../css/color";
import { userLocationsAtom } from "../../jotai/atoms";
import { useSpaceInfo, useUserInfo } from "../../liveblocks/maps";
import { styled } from "../../stitches.config";
import { AnyBlock, CursorType, Presence, User } from "../../types";
import { DefaultCursor } from "./DefaultCursor";
import Stream from "../../av/Stream";
import { useStampSound } from "../../av/useStamp";

export const Cursor = React.memo(function Cursor({
  stream,
  userId,
  isMuted,
  upsertBlock,
  stampMode,
  cursor,
  position,
}: {
  stream?: MediaStreamTrack;
  userId: string;
  isMuted: boolean;
  upsertBlock: (block: Partial<AnyBlock>) => void;
  stampMode: Presence["stampMode"];
  cursor: Presence["cursor"];
  position?: Presence["position"];
}) {
  useStampSound(stampMode);

  const [userInfo] = useUserInfo(userId);
  const [userLocations] = useAtom(userLocationsAtom);
  const userLocation = userLocations[userId];
  const [spaceInfo] = useSpaceInfo();
  const cursorSize = spaceInfo?.cursorSize || DEFAULT_CURSOR_SIZE;
  const cursorPosRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    if (position) {
      cursorPosRef.current = position;
    }
  }, [position]);

  if (!userInfo) return <></>;

  const isElsewhere = userLocation && userLocation.block;
  const isStreamDocked = !!(stream && isElsewhere);
  const isStreamNearCursor = stream && !isStreamDocked;

  const activeStampMode = !!(
    stampMode !== "inactive" &&
    stream &&
    !userLocation?.locked
  );

  const style = {
    width: cursorSize + DEFAULT_CURSOR_OFFSET * 2,
    height: cursorSize + DEFAULT_CURSOR_OFFSET * 2,
    opacity: activeStampMode ? 0 : 1,
    transform: `translate(${position?.x ?? 0}px, ${position?.y ?? 0}px)`,
  };

  return (
    <div style={{ pointerEvents: "none" }} className="cursor">
      {stream ? (
        <Stream
          cursorPosRef={cursorPosRef}
          location={userLocation}
          color={userInfo.color}
          userId={userId}
          activeStampMode={activeStampMode}
          stream={stream}
          stampMode={stampMode}
          upsertBlock={upsertBlock}
        />
      ) : null}
      <CursorContainer style={style}>
        <CursorName
          isStreamNearCursor={isStreamNearCursor}
          userInfo={userInfo}
          cursorSize={cursorSize}
          isMuted={isMuted}
          cursor={cursor}
        />
      </CursorContainer>
    </div>
  );
});

export const CursorName = React.memo(function CursorName({
  isStreamNearCursor,
  userInfo,
  cursorSize,
  isMuted,
  cursor,
}: {
  isStreamNearCursor?: boolean;
  userInfo: User;
  cursorSize: number;
  isMuted: boolean;
  cursor: CursorType;
}) {
  return (
    <>
      <motion.div
        style={{ position: "absolute" }}
        animate={{
          top: isStreamNearCursor ? cursorSize : "1rem",
          left: isStreamNearCursor ? "auto" : "1rem",
        }}
        transition={DEFAULT_SPRING}
      >
        <CursorNameTag
          name={userInfo.name}
          color={userInfo.color}
          isMuted={isMuted}
        />
      </motion.div>
      <DefaultCursor color={colorValue(userInfo.color, 80)} cursor={cursor} />
    </>
  );
});

export const CursorNameTag = React.memo(function CursorNameTag({
  name,
  color,
  isMuted,
  ...props
}: {
  name: string;
  color: User["color"];
  isMuted: boolean;
  props?: ReactPropTypes;
}) {
  return (
    <NameTag className={`nametag ${name}`} {...props}>
      <NameCaption
        style={{
          backgroundColor: colorValue(color, 80),
        }}
      >
        {name}
        {isMuted && <MuteIcon icon="MicOff" />}
      </NameCaption>
    </NameTag>
  );
});

const CursorContainer = styled("figure", {
  position: "absolute",
  top: 0,
  left: 0,
  display: "grid",
  justifyItems: "center",
  zIndex: "$cursor",
  lineHeight: "$4",
  filter: "drop-shadow(0px 2px 4px rgba(113, 81, 78, 0.5))",
  transitionProperty: "opacity",
  transitionDuration: "0.1s",
  willChange: "transform",
});

const NameCaption = styled("figcaption", {
  display: "grid",
  gridAutoFlow: "column",
  gap: "$1",
  placeItems: "center",
  borderRadius: "4px",
  border: "1px solid $paper30",
  padding: "$1 6px",
  fontSize: "14px",
  lineHeight: "1rem",
});

export const NameTag = styled("div", {
  background: "white",
  border: "2px solid #FFFFFF",
  fontSize: "$4",
  borderRadius: "6px",
  color: "$paper30",
  textAlign: "center",
  whiteSpace: "nowrap",
  "&::before": {
    content: `""`,
    display: "block",
    position: "absolute",
    top: "0",
    bottom: "0",
    right: "0",
    left: "0",
  },
});

export const MuteIcon = styled(Icon, {
  marginRight: "-$1",
});
